import { api } from "@stoneflow/backend/convex/_generated/api"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { Plus } from "lucide-react"
import { useMemo } from "react"
import { toast } from "sonner"
import { NeedsAttentionCard } from "#/components/admin/needs-attention-card"
import { TenantListTable, type TenantRow } from "#/components/admin/tenant-list-table"
import { CreateTenantDialog } from "#/components/create-tenant-dialog"
import { RouteError } from "#/components/route-error"
import { Button } from "#/components/ui/button"
import { Skeleton } from "#/components/ui/skeleton"
import { useAppNavigate } from "#/hooks/use-app-navigate"
import { useIsPlatformOperator } from "#/hooks/useIsTenantAdmin"

export const Route = createFileRoute("/_authenticated/admin/tenants/")({
	errorComponent: RouteError,
	component: TenantsPage,
})

function TenantsPage() {
	const { isOperator } = useIsPlatformOperator()
	const navigate = useAppNavigate()
	const tenants = useQuery(api.platform.tenants.list, isOperator ? {} : "skip")
	const atRisk = useQuery(api.platform.billing.listTenantsBillingSummary, isOperator ? {} : "skip")
	const setStatus = useMutation(api.platform.tenants.setStatus)

	const rows: TenantRow[] | null = useMemo(() => {
		if (!tenants) return null
		return tenants.map((t) => ({
			orgId: t.orgId,
			name: t.name,
			status: t.status,
			plan: t.plan,
			userCount: t.userCount,
			logCount: t.logCount,
			createdAt: t.createdAt,
			modules: t.modules,
			billing: t.billing,
			exceptionCount: t.exceptionCount,
		}))
	}, [tenants])

	if (!isOperator) {
		return (
			<div className="py-12 text-center text-sm text-foreground-muted">
				This page is for platform operators.
			</div>
		)
	}

	if (rows === null) {
		return (
			<div className="space-y-3">
				<Skeleton className="h-9 w-48" />
				<Skeleton className="h-10 w-full" />
				<Skeleton className="h-10 w-full" />
				<Skeleton className="h-10 w-full" />
			</div>
		)
	}

	const handleChangeStatus = async (orgId: string, status: "active" | "trial" | "disabled") => {
		try {
			await setStatus({ orgId, status })
			toast.success(`Tenant marked ${status}`)
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Failed to update status")
		}
	}

	return (
		<div className="space-y-4">
			<header className="flex items-center justify-between">
				<div>
					<h1>Tenants</h1>
					<p className="text-sm text-foreground-muted">
						Super-admin view of all tenants on the platform.
					</p>
				</div>
				<CreateTenantDialog
					trigger={
						<Button>
							<Plus className="mr-1 size-4" />
							New tenant
						</Button>
					}
				/>
			</header>

			{atRisk && atRisk.length > 0 ? (
				<NeedsAttentionCard
					rows={atRisk}
					onSelect={(orgId) => navigate({ to: "/admin/tenants/$orgId", params: { orgId } })}
				/>
			) : null}

			<TenantListTable
				tenants={rows}
				onSelectTenant={(orgId) => navigate({ to: "/admin/tenants/$orgId", params: { orgId } })}
				onChangeStatus={handleChangeStatus}
				isPlatformOperator={isOperator}
			/>
		</div>
	)
}
